// File: src/components/common/Button.tsx
import React, { ButtonHTMLAttributes } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'outline' | 'none' | 'success'; // Added 'success' variant
  size?: 'xs' | 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  fullWidth?: boolean;
  // className is part of ButtonHTMLAttributes
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  fullWidth = false,
  className = '',
  ...props
}) => {
  const baseStyles = "relative inline-flex items-center justify-center font-semibold rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition ease-in-out duration-150";
  
  let variantStyles = '';
  switch (variant) {
    case 'primary':
      variantStyles = 'bg-primary text-white hover:bg-primary-dark focus:ring-primary';
      break;
    case 'secondary':
      variantStyles = 'bg-secondary text-white hover:bg-secondary-dark focus:ring-secondary';
      break;
    case 'danger':
      variantStyles = 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500';
      break;
    case 'success': // New 'success' variant
      variantStyles = 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500';
      break;
    case 'outline':
      variantStyles = 'bg-transparent text-primary border border-primary hover:bg-primary-light hover:text-primary-dark focus:ring-primary';
      break;
    case 'none':
      variantStyles = '';
      break;
    default:
      variantStyles = 'bg-primary text-white hover:bg-primary-dark focus:ring-primary';
  }

  let sizeStyles = '';
  switch (size) {
    case 'xs':
      sizeStyles = 'py-1 px-2 text-xs';
      break;
    case 'sm':
      sizeStyles = 'py-1.5 px-3 text-sm';
      break;
    case 'md':
      sizeStyles = 'py-2 px-4 text-base';
      break;
    case 'lg':
      sizeStyles = 'py-2.5 px-6 text-lg';
      break;
    default:
      sizeStyles = 'py-2 px-4 text-base';
  }

  const widthStyles = fullWidth ? 'w-full' : '';

  const spinner = (
    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  );

  return (
    <button
      className={`${baseStyles} ${variantStyles} ${sizeStyles} ${widthStyles} ${isLoading ? 'opacity-75 cursor-not-allowed' : ''} ${className}`}
      disabled={isLoading || props.disabled}
      {...props}
    >
      {isLoading && (
        <span className="absolute inset-0 flex items-center justify-center">
          {spinner}
        </span>
      )}
      <span className={`flex items-center justify-center ${isLoading ? 'opacity-0' : 'opacity-100'}`}>
        {children}
      </span>
    </button>
  );
};

export default Button;